import { DestroyRef, Directive, Inject, InjectionToken, Input, OnInit, Optional } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { selectors } from '@twaice-fe/frontend/shared/store';
import { differenceInCalendarDays, isValid } from 'date-fns';
import { NzDatePickerComponent } from 'ng-zorro-antd/date-picker';
import { map, Observable } from 'rxjs';

const { systemSelectors } = selectors;

/** For pages spanning several systems, where no single selected system's installation date applies. */
export const INSTALLATION_DATE_OVERRIDE = new InjectionToken<Observable<string | null>>('INSTALLATION_DATE_OVERRIDE');

@Directive({
  selector: 'nz-date-picker[twaiceClampDateRange],nz-range-picker[twaiceClampDateRange]',
})
export class ClampDateRangeDirective implements OnInit {
  @Input() disableDateRangeClamp = false;
  @Input() useDataExplorerDate = false;

  constructor(
    private datePicker: NzDatePickerComponent,
    private destroy$: DestroyRef,
    private store: Store,
    @Optional() @Inject(INSTALLATION_DATE_OVERRIDE) private installationDateOverride$: Observable<string | null> | null
  ) {}

  ngOnInit(): void {
    const installationDate$ =
      this.installationDateOverride$ ??
      this.store
        .select(systemSelectors.getSelected)
        .pipe(map((system) => system?.dateFirstDataAvailable ?? system?.bessMetadata?.commercialOperationDate ?? null));

    installationDate$.pipe(takeUntilDestroyed(this.destroy$)).subscribe((installationDate) => {
      if (this.disableDateRangeClamp) {
        this.datePicker.nzDisabledDate = () => false;
        return;
      }

      const now = new Date();
      const todayUtcAsLocal = new Date(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        now.getUTCHours(),
        now.getUTCMinutes(),
        now.getUTCSeconds()
      );

      // clamp enabled dates
      this.datePicker.nzDisabledDate = (current: Date) => {
        const endCondition = this.useDataExplorerDate ? false : differenceInCalendarDays(current, todayUtcAsLocal) > 0;

        // if the installation date is valid we clamp the date to after the installation date
        if (installationDate && isValid(new Date(installationDate))) {
          return endCondition || differenceInCalendarDays(current, new Date(installationDate)) < 0;
        }

        // else we just clamp it to the current date
        return endCondition;
      };
    });
  }
}
